import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchLnpPage, PaginatedLnp } from "../../../api/lnp";
import { checkInProduct } from "../../../api/migration";
import type { ILnp } from "../../../type";
import { extractApiError } from "../helpers";

const EMPTY_PAGE: PaginatedLnp = {
  count: 0,
  next: null,
  previous: null,
  results: [],
};

interface UseBrowseLnpResult {
  rows: ILnp[];
  count: number;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  inProductPks: Set<number>;
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

/**
 * Loads one page of beta Lnp rows (via TextSearch) and annotates them with
 * product-side existence so the caller can badge / dim "already shipped"
 * rows. Applies the seq-ref stale-response guard pattern that the rest of
 * the admin hooks use (`useAdminUsers` is the reference implementation).
 *
 * The in-product set is accumulative across pages — once we learn that
 * beta#102 exists on product, we keep that fact even after the user
 * scrolls away from page 1. This matters when rows stay in the cart after
 * leaving the page they were picked from.
 */
export default function useBrowseLnp(
  search: string,
  active: boolean
): UseBrowseLnpResult {
  const [rows, setRows] = useState<ILnp[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [inProductPks, setInProductPks] = useState<Set<number>>(new Set());
  // Initial true: the first render with the block-spinner gate (see
  // MigrationBrowserPanel) would otherwise flash "No rows match." before
  // the first fetch's setIsLoading(true) landed. Starting true means the
  // first paint is the spinner; inactive tabs never run reload so the
  // value stays true until the tab activates and the fetch resolves.
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const seqRef = useRef(0);

  // Reset to page 1 whenever the search text or size changes.
  useEffect(() => {
    setPage(1);
  }, [search, pageSize]);

  const reload = useCallback(async () => {
    if (!active) return;
    const mySeq = ++seqRef.current;
    setIsLoading(true);
    setError(null);
    try {
      // DRF's PageNumberPagination answers 404 (not 200+empty) when the
      // requested page is past the last one. This happens transiently
      // when the search text changes: the reload effect can fire with a
      // stale high page number before the page-reset effect lands. We
      // treat that 404 as "empty page" so the next render's corrected
      // reload picks up page 1 cleanly. Non-404 errors still propagate.
      let data: PaginatedLnp;
      try {
        data = await fetchLnpPage({
          page,
          pageSize,
          search: search || undefined,
          ordering: "index",
          status: "draft",
        });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          data = EMPTY_PAGE;
        } else {
          throw err;
        }
      }
      if (mySeq !== seqRef.current) return;
      setRows(data.results);
      setCount(data.count);

      // Merge in-product status for the new page. Additive — previous
      // pages' learnings are kept so badges on cart rows stay correct
      // across pagination.
      const pks = data.results.map((r) => r.index);
      if (pks.length > 0) {
        try {
          const hits = await checkInProduct(pks);
          if (mySeq !== seqRef.current) return;
          setInProductPks((prev) => {
            const next = new Set(prev);
            hits.forEach((v) => next.add(v));
            return next;
          });
        } catch {
          // Non-fatal — badges are decoration, not correctness.
        }
      }
    } catch (err) {
      if (mySeq !== seqRef.current) return;
      setError(extractApiError(err, "Failed to load rows."));
    } finally {
      if (mySeq === seqRef.current) setIsLoading(false);
    }
  }, [page, pageSize, search, active]);

  useEffect(() => {
    reload();
  }, [reload]);

  return {
    rows,
    count,
    page,
    setPage,
    pageSize,
    setPageSize,
    inProductPks,
    isLoading,
    error,
    reload,
  };
}
